import _React, { useState, useEffect } from 'react'
import QRCode from 'qrcode'

interface WalletQRCodeProps {
  walletAddress: string
  size?: number
  /** Optional USDT amount displayed visually below QR (not encoded — no universal token URI standard) */
  amount?: number
}

/**
 * Generates and displays a QR code for a crypto wallet address.
 * Uses the existing `qrcode` package with high error correction for reliable mobile scanning.
 * Styled to match the dark background / gold accent theme.
 * QR encodes address only (wallets like SafePal/Trust don't support amount params for USDT).
 * Amount is shown as prominent visual text so the user knows what to send.
 */
export default function WalletQRCode({ walletAddress, size = 180, amount }: WalletQRCodeProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!walletAddress) {
      setQrDataUrl(null)
      return
    }

    let cancelled = false

    // QR encodes address only — no universal URI standard for USDT token amounts
    QRCode.toDataURL(walletAddress, {
      errorCorrectionLevel: 'H',
      margin: 2,
      width: size,
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch((err) => {
        console.error('WalletQRCode generation failed:', err)
      })

    return () => { cancelled = true }
  }, [walletAddress, size])

  if (!qrDataUrl) return null

  return (
    <div className="flex flex-col items-center mb-3">
      {amount != null && amount > 0 && (
        <span className="text-lg font-bold text-green-400 mb-1">
          ${amount.toFixed(2)} USDT
        </span>
      )}
      <div
        className="rounded-lg border-2 border-amber-600/40 p-2 bg-gray-900"
        style={{ boxShadow: '0 0 12px rgba(212, 175, 55, 0.25)' }}
      >
        <img
          src={qrDataUrl}
          alt="Wallet QR Code"
          width={size}
          height={size}
          className="block rounded"
          style={{ imageRendering: 'pixelated' }}
        />
      </div>
      <span className="text-xs text-gray-400 mt-1">Scan to get wallet address</span>
    </div>
  )
}
